import { fetchWithAuth } from '@/lib/fetchWithAuth';

export interface InterBankStatus {
  configured: boolean;
  connected: boolean;
  sandbox: boolean;
  apiBase: string;
  contaCorrente: string | null;
  scopes: string[];
  saldo?: InterSaldo;
  error?: string;
  detail?: unknown;
  message?: string;
}

export interface InterSaldo {
  disponivel?: number;
  bloqueadoCheque?: number;
  bloqueadoJudicialmente?: number;
  bloqueadoAdministrativo?: number;
  limite?: number;
  dataSaldo?: string;
}

export interface InterTransacaoDetalhes {
  nomePagador?: string;
  nomeRecebedor?: string;
  descricaoPix?: string;
  endToEndId?: string;
  chavePixRecebedor?: string;
  tipoDetalhe?: string;
}

export interface InterTransacao {
  dataEntrada?: string;
  dataTransacao?: string;
  tipoTransacao?: string;
  tipoOperacao?: 'C' | 'D' | string;
  valor?: string | number;
  titulo?: string;
  descricao?: string;
  detalhes?: string | InterTransacaoDetalhes;
}

export interface InterExtrato {
  transacoes?: InterTransacao[];
  totalElementos?: number;
  totalPaginas?: number;
  ultimaPagina?: boolean;
  primeiraPagina?: boolean;
  numeroPagina?: number;
  tamanhoPagina?: number;
}

export interface InterCobranca {
  codigoSolicitacao?: string;
  seuNumero?: string;
  situacao?: string;
  dataEmissao?: string;
  dataVencimento?: string;
  valorNominal?: number;
  pagador?: { nome?: string; cpfCnpj?: string };
}

export interface EmitirCobrancaPayload {
  seuNumero: string;
  valorNominal: number;
  dataVencimento: string;
  numDiasAgenda?: number;
  mensagem?: string;
  pagador: {
    nome: string;
    cpfCnpj: string;
    tipoPessoa?: 'FISICA' | 'JURIDICA';
    email?: string;
    endereco?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
    cep?: string;
    ddd?: string;
    telefone?: string;
  };
}

export interface EmitirCobrancaResponse {
  codigoSolicitacao?: string;
  status?: string;
}

export interface InterCobrancasResponse {
  cobrancas?: InterCobranca[];
  totalElementos?: number;
}

export interface InterReconcileStats {
  total?: number;
  criadasPagar?: number;
  criadasReceber?: number;
  conciliadasPagar?: number;
  conciliadasReceber?: number;
  ambiguas?: number;
  jaConciliadas?: number;
  ignoradas?: number;
  erros?: number;
  error?: string;
}

export interface InterSyncStatus {
  syncVersion: number;
  lastNotificationAt?: string;
  lastSyncAt?: string;
  lastSyncTrigger?: string;
  lastError?: string;
  lastReconcileAt?: string;
  lastReconcileStats?: InterReconcileStats;
  saldo?: InterSaldo;
  extrato?: InterExtrato;
}

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Erro ${response.status}`);
  }
  return response.json();
}

export const interBankService = {
  async getStatus(): Promise<InterBankStatus> {
    const response = await fetchWithAuth('/api/inter-bank/status');
    return parseJson(response);
  },

  async getSaldo(dataSaldo?: string): Promise<InterSaldo> {
    const qs = dataSaldo ? `?dataSaldo=${encodeURIComponent(dataSaldo)}` : '';
    const response = await fetchWithAuth(`/api/inter-bank/saldo${qs}`);
    return parseJson(response);
  },

  async getExtrato(params: {
    dataInicio: string;
    dataFim: string;
    pagina?: number;
    tamanhoPagina?: number;
    tipoOperacao?: string;
    tipoTransacao?: string;
  }): Promise<InterExtrato> {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        search.set(key, String(value));
      }
    });
    const response = await fetchWithAuth(`/api/inter-bank/extrato?${search}`);
    return parseJson(response);
  },

  async getExtratoCompleto(params: {
    dataInicio: string;
    dataFim: string;
    pagina?: number;
    tamanhoPagina?: number;
  }): Promise<InterExtrato> {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        search.set(key, String(value));
      }
    });
    const response = await fetchWithAuth(`/api/inter-bank/extrato/completo?${search}`);
    return parseJson(response);
  },

  async exportarExtratoPdf(dataInicio: string, dataFim: string): Promise<Blob> {
    const response = await fetchWithAuth(
      `/api/inter-bank/extrato/exportar?dataInicio=${encodeURIComponent(dataInicio)}&dataFim=${encodeURIComponent(dataFim)}`,
      { headers: { Accept: 'application/pdf' } }
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Erro ao exportar extrato');
    }
    return response.blob();
  },

  async listarCobrancas(params: {
    dataInicial: string;
    dataFinal: string;
    situacao?: string;
    pagina?: number;
    tamanhoPagina?: number;
  }): Promise<InterCobrancasResponse> {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        search.set(key, String(value));
      }
    });
    const response = await fetchWithAuth(`/api/inter-bank/cobrancas?${search}`);
    return parseJson(response);
  },

  async emitirCobranca(payload: EmitirCobrancaPayload): Promise<EmitirCobrancaResponse> {
    const response = await fetchWithAuth('/api/inter-bank/cobrancas', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return parseJson(response);
  },

  async baixarCobrancaPdf(codigoSolicitacao: string): Promise<Blob> {
    const response = await fetchWithAuth(`/api/inter-bank/cobrancas/${encodeURIComponent(codigoSolicitacao)}/pdf`, {
      headers: { Accept: 'application/pdf' },
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Erro ao baixar PDF do boleto');
    }
    return response.blob();
  },

  async cancelarCobranca(codigoSolicitacao: string, motivoCancelamento?: string): Promise<unknown> {
    const response = await fetchWithAuth(
      `/api/inter-bank/cobrancas/${encodeURIComponent(codigoSolicitacao)}/cancelar`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivoCancelamento: motivoCancelamento || 'ACERTOS' }),
      }
    );
    return parseJson(response);
  },

  async getSyncStatus(): Promise<InterSyncStatus> {
    const response = await fetchWithAuth('/api/inter-bank/sync-status');
    return parseJson(response);
  },

  async sincronizarAgora(): Promise<unknown> {
    const response = await fetchWithAuth('/api/inter-bank/sync', { method: 'POST' });
    return parseJson(response);
  },

  async registrarWebhooks(webhookBaseUrl?: string): Promise<unknown> {
    const response = await fetchWithAuth('/api/inter-bank/webhooks/registrar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(webhookBaseUrl ? { webhookBaseUrl } : {}),
    });
    return parseJson(response);
  },
};
